import { format, isAfter, startOfDay } from "date-fns";

import { repeatTypeEnumNever } from "../../../../types/types";
import {
  describeFrequency,
  ExistingSchedule,
  getFirstOccurrence,
  getFormFromSchedule,
  REPEAT_ON_IDS,
} from "./recurringSchedule";

export type SeriesWork = {
  workId: string;
  startDate: string;
  dueDate: string;
  repeatType?: string | null;
  repeats?: number | null;
};

export type ScheduleSource = ExistingSchedule & {
  startDate: Date | string;
  dueDate: Date | string;
  stopRecurring?: boolean;
  repeatTypeEnum?: { repeatType?: string | null } | null;
};

export type SeriesStatus = {
  isRecurring: boolean;
  /** The newest work item in the series (it carries the schedule). */
  latestWork?: SeriesWork;
  /** "Weekly", "Every 2 weeks", ... */
  cadence: string;
  /** "Next occurrence Sep 10, 2026", "Next one when the current one is completed", ... */
  nextText: string;
};

const capitalize = (text: string) =>
  text ? text[0].toUpperCase() + text.slice(1) : text;

/** The series sorted by due date (then start date), oldest first. */
export const sortSeriesByDue = <T extends SeriesWork>(works: T[]) =>
  [...works].sort(
    (a, b) =>
      new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime() ||
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
  );

/**
 * Whether a work item's series is still recurring, how often, and when the
 * next copy comes. The newest copy carries the schedule; older copies keep
 * their old repeat type after it is stopped.
 */
export const getSeriesStatus = (
  series: SeriesWork[],
  work: ScheduleSource | undefined,
  today: Date = new Date(),
): SeriesStatus => {
  const latestWork = sortSeriesByDue(series).pop();
  const isRecurring = latestWork
    ? !!latestWork.repeatType && latestWork.repeatType !== "Never"
    : !!work &&
      !!work.repeatTypeEnumId &&
      work.repeatTypeEnumId.toLowerCase() !== repeatTypeEnumNever.id &&
      !work.stopRecurring;

  if (!isRecurring || !work) {
    return { isRecurring: false, latestWork, cadence: "", nextText: "" };
  }

  const cadence = capitalize(
    describeFrequency(
      latestWork?.repeatType ?? work.repeatTypeEnum?.repeatType,
      latestWork?.repeats ?? work.repeatValue,
    ),
  );
  const repeatOnId = work.repeatOnId?.toLowerCase();

  if (repeatOnId === REPEAT_ON_IDS.immediately) {
    return {
      isRecurring,
      latestWork,
      cadence,
      nextText: "All occurrences created",
    };
  }
  if (repeatOnId === REPEAT_ON_IDS.completion) {
    return {
      isRecurring,
      latestWork,
      cadence,
      nextText: "Next one when the current one is completed",
    };
  }

  const anchor = latestWork ?? work;
  const next = getFirstOccurrence(
    getFormFromSchedule(
      { ...work, repeatValue: latestWork?.repeats ?? work.repeatValue },
      anchor,
      today,
    ),
    anchor,
    today,
  );
  const until = work.repeatUntil
    ? startOfDay(new Date(work.repeatUntil))
    : null;
  const nextText =
    next && !(until && isAfter(next.startDate, until))
      ? `Next occurrence ${format(next.startDate, "MMM d, yyyy")}`
      : "No more occurrences scheduled";

  return { isRecurring, latestWork, cadence, nextText };
};

/**
 * The rows the Repeats tab lists (and counts: the tab badge uses the same
 * rows, QA A8). The server always returns the work item itself; when it is
 * the only row and isn't on a schedule, it isn't a repeat, so nothing is
 * listed ("No occurrences yet") and the badge says 0. A work item alone in a
 * recurring series is listed and counted (1).
 */
export const repeatListRows = <
  T extends { workId?: string | null; repeatType?: string | null },
>(
  rows: T[] | undefined,
  workId: string | undefined,
): T[] | undefined => {
  if (!rows) return rows;
  if (
    rows.length === 1 &&
    !!workId &&
    rows[0].workId?.toLowerCase() === workId.toLowerCase() &&
    (!rows[0].repeatType || rows[0].repeatType === "Never")
  )
    return [];
  return rows;
};
